import { css } from "@digits-css"
import { BLUR_MD } from "@digits-shared/themes/blur"
import colors from "@digits-shared/themes/colors"
import "./Glass.digits.css"

// Figma authors these glass gradients in normalized space: their axis adapts to
// the element's aspect ratio. A fixed CSS gradient angle only matches one
// aspect, so these SVG gradients use objectBoundingBox units and stretch with
// the box the same way as Figma's gradientTransform. Keep encodeURIComponent
// in the exported value initializer: the digits-css extractor cannot call local
// helper functions while resolving CSS interpolation values.

const GLASS_SHEEN_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg">
    <linearGradient
      id="g"
      gradientUnits="objectBoundingBox"
      x1="0.0752"
      y1="0.0854"
      x2="0.6542"
      y2="0.7424"
    >
      <stop offset="0" stop-color="white" stop-opacity="0"/>
      <stop offset="1" stop-color="white" stop-opacity="0.1"/>
    </linearGradient>
    <rect width="100%" height="100%" fill="url(#g)"/>
  </svg>
`

export const GLASS_SHEEN_BACKGROUND = `url("data:image/svg+xml;utf8,${encodeURIComponent(
  GLASS_SHEEN_SVG
)}")`

const GLASS_STROKE_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg">
    <linearGradient
      id="g"
      gradientUnits="objectBoundingBox"
      x1="0.0305"
      y1="0.0510"
      x2="0.5531"
      y2="0.9244"
    >
      <stop offset="0" stop-color="white" stop-opacity="0.7"/>
      <stop offset="0.4057" stop-color="white" stop-opacity="0"/>
      <stop offset="0.5744" stop-color="white" stop-opacity="0"/>
      <stop offset="1" stop-color="white" stop-opacity="0.4"/>
    </linearGradient>
    <rect width="100%" height="100%" fill="url(#g)"/>
  </svg>
`

export const GLASS_STROKE_BACKGROUND = `url("data:image/svg+xml;utf8,${encodeURIComponent(
  GLASS_STROKE_SVG
)}")`

export const GLASS_GRADIENTS = css`
  background-image:
    radial-gradient(
      52.43% 57.5% at 50% 100%,
      ${colors.translucentWhite40} 0%,
      ${colors.transparent} 100%
    ),
    linear-gradient(139deg, ${colors.transparent} 8.06%, ${colors.translucentWhite10} 70.11%);
`

/**
 * Figma's `Glass Fill`: the sheen gradient over a translucent white base, with
 * no radial highlight. For glass surfaces sitting on a coloured backdrop that
 * should read through them — pair with {@link GLASS_STROKE_BORDER}.
 */
export const GLASS_FILL = css`
  background:
    linear-gradient(139deg, ${colors.transparent} 8.06%, ${colors.translucentWhite10} 70.11%),
    ${colors.translucentWhite20};
`

export const GLASS_LAYER = css`
  background: ${colors.translucentWhite40};
  border: 1px solid ${colors.translucentWhite40};
  background-blend-mode: luminosity;
  backdrop-filter: blur(${BLUR_MD});
`

const GLASS_STROKE_MASK_STYLES = css`
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  // The pseudo-element covers the whole surface; these paired masks subtract
  // the content box from the border box so only the padded stroke area paints.
  mask:
    linear-gradient(white 0, white 0) content-box,
    linear-gradient(white 0, white 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
`

export const GLASS_STROKE_STYLES = css`
  background: linear-gradient(
    149deg,
    ${colors.translucentWhite70} 4.33%,
    ${colors.transparent} 34.44%,
    ${colors.transparent} 46.96%,
    ${colors.translucentWhite40} 78.54%
  );
  ${GLASS_STROKE_MASK_STYLES}
`

const GLASS_STROKE_EDGE_STYLES = css`
  background: ${GLASS_STROKE_BACKGROUND};
  background-size: 100% 100%;
  ${GLASS_STROKE_MASK_STYLES}
`

/**
 * 1px glass stroke drawn via ::before in a reserved transparent border, so the
 * ring sits beside content (the gradient renders in the border box). Parent must
 * be `position: relative` with a `border-radius` (inherited by ::before).
 */
export const GLASS_STROKE_BORDER = css`
  border: 1px solid transparent;
  box-sizing: content-box;

  &::before {
    padding: 1px;
    ${GLASS_STROKE_STYLES}
  }
`

/**
 * 1px glass stroke drawn over the element edge. Use when the background needs
 * to paint to the outer edge and a reserved transparent border would show as a
 * visible band outside the ring.
 */
export const GLASS_STROKE_EDGE_OVERLAY = css`
  &::before {
    padding: 1px;
    ${GLASS_STROKE_EDGE_STYLES}
  }
`

/**
 * 1.5px glass stroke drawn via ::after so it paints over content that fills the
 * element (e.g. a circular `<img>`), where a ::before ring would be hidden
 * underneath. Parent must be `position: relative` with a `border-radius`.
 */
export const GLASS_STROKE_OVERLAY = css`
  &::after {
    padding: 1.5px;
    ${GLASS_STROKE_STYLES}
  }
`
